/**
 * Spreadsheet enrichment operations: run enrichment, stream reconnection,
 * sheet repaint for custom cell highlights, and force redraw of enriching cells.
 */

import { IRenderManagerService } from '@univerjs/engine-render';
import type { Cell } from '../../api/spreadsheet';
import {
  displayColumnName,
  DOMAIN_COLUMN_NAME,
  executeCells,
  getActiveCellExecutorWorkflowId,
  isLockedColumn,
  putCells,
  streamExecuteCells
} from '../../api/spreadsheet';
import type { CampaignSpreadsheetViewType } from '../../context/CampaignContext';
import type {
  EnrichmentCell,
  EnrichmentHandlersContext,
  EnrichmentRepaintContext,
  EnrichmentSelection,
  EnrichmentSelectionContext,
  ForceEnrichingRedrawContext,
  HeaderStateLike,
  ReconnectEnrichmentContext,
  RunEnrichmentContext,
  SheetLike,
  UniverSheetAPILike,
  ColumnSchemaOverridesLike
} from './spreadsheetTypes';
import type { ColumnSchemaWithData } from './spreadsheetUtils';
import {
  columnDefinitionHasEnrichmentPrompt,
  getColTypeFromSchema,
  getColumnDefinition,
  normalizeCellValue
} from './spreadsheetUtils';

export type {
  EnrichmentCell,
  EnrichmentHandlersContext,
  EnrichmentRepaintContext,
  EnrichmentSelection,
  EnrichmentSelectionContext,
  ForceEnrichingRedrawContext,
  ReconnectEnrichmentContext,
  RunEnrichmentContext
};

/**
 * Get the set of row indices that are filtered out (hidden) by the sheet filter.
 * Returns null if there is no filter. Uses Univer sheets-filter facade (getFilter().getFilteredOutRows()).
 */
export function getFilteredOutRowSet(
  api: UniverSheetAPILike | null
): Set<number> | null {
  if (!api) return null;
  const fWorksheet = api.getActiveWorkbook()?.getActiveSheet?.() as
    | { getFilter?: () => { getFilteredOutRows?: () => number[] } }
    | null
    | undefined;
  const fFilter = fWorksheet?.getFilter?.();
  const rows = fFilter?.getFilteredOutRows?.();
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return new Set(rows);
}

/**
 * Get the row indices that are filtered out (hidden) by the sheet filter, for use by export/sync.
 * Returns null if there is no filter (caller should treat all rows as visible).
 */
export function getFilteredOutRowIds(
  api: UniverSheetAPILike | null
): number[] | null {
  const set = getFilteredOutRowSet(api);
  return set == null ? null : Array.from(set);
}

type ParsedSheetRange = {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
};

function parseFRangeToSheetRange(
  fRange: { getRange?: () => unknown } | null | undefined
): ParsedSheetRange | null {
  const rangeData =
    typeof fRange?.getRange === 'function' ? fRange.getRange() : null;
  if (
    !rangeData ||
    typeof rangeData !== 'object' ||
    !('startRow' in rangeData) ||
    !('endRow' in rangeData)
  )
    return null;
  return rangeData as ParsedSheetRange;
}

/** Collect all selected rectangles from Univer (ctrl-click uses multiple ranges; getActiveRange() is only the active one). */
function getSelectedSheetRanges(api: UniverSheetAPILike): ParsedSheetRange[] {
  const fSheet = api.getActiveWorkbook()?.getActiveSheet?.();
  if (!fSheet) return [];
  const fSelection = fSheet.getSelection?.();
  const rangeList = fSelection?.getActiveRangeList?.();
  const ranges: ParsedSheetRange[] = [];
  if (Array.isArray(rangeList) && rangeList.length > 0) {
    for (const fr of rangeList) {
      const r = parseFRangeToSheetRange(fr as { getRange?: () => unknown });
      if (r) ranges.push(r);
    }
  }
  if (ranges.length > 0) return ranges;
  const fallback =
    parseFRangeToSheetRange(fSelection?.getActiveRange?.() ?? null) ??
    parseFRangeToSheetRange(fSheet.getActiveRange?.() ?? null);
  return fallback ? [fallback] : [];
}

function sortUniqueColumnNames(
  names: string[],
  columnNamesByIndex: string[]
): string[] {
  const uniq = Array.from(new Set(names));
  uniq.sort(
    (a, b) => columnNamesByIndex.indexOf(a) - columnNamesByIndex.indexOf(b)
  );
  return uniq;
}

/** Find cells to enrich from the provided Univer ranges */
function expandParsedRangesToExplicitCells(
  ranges: ParsedSheetRange[],
  headerState: HeaderStateLike,
  filteredOutSet: Set<number> | null
): EnrichmentCell[] | null {
  const maxCol = headerState.columnNamesByIndex.length - 1;
  const seen = new Set<string>();
  const explicitCells: EnrichmentCell[] = [];
  for (const range of ranges) {
    const startRow = Math.max(0, range.startRow);
    const endRow = Math.max(startRow, range.endRow);
    const startCol = Math.max(0, range.startColumn);
    const endCol = Math.min(range.endColumn, maxCol);
    if (endRow < startRow || endCol < startCol) continue;
    for (let r = startRow; r <= endRow; r++) {
      if (filteredOutSet != null && filteredOutSet.has(r)) continue;
      for (let c = startCol; c <= endCol; c++) {
        const name = headerState.columnNamesByIndex[c];
        if (name == null) continue;
        const key = `${r}:${name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        explicitCells.push({ row_id: r, column_name: name });
      }
    }
  }
  return explicitCells.length === 0 ? null : explicitCells;
}

function selectionFromSingleRectangle(
  range: ParsedSheetRange,
  headerState: HeaderStateLike,
  filteredOutSet: Set<number> | null
): EnrichmentSelection | null {
  const startRow = Math.max(0, range.startRow);
  const endRow = Math.max(startRow, range.endRow);
  const startCol = Math.max(0, range.startColumn);
  const endCol = Math.min(
    range.endColumn,
    headerState.columnNamesByIndex.length - 1
  );
  if (endRow < startRow || endCol < startCol) return null;
  const row_ids: number[] = [];
  for (let r = startRow; r <= endRow; r++) {
    if (filteredOutSet != null && filteredOutSet.has(r)) continue;
    row_ids.push(r);
  }
  const column_names: string[] = [];
  const seen = new Set<string>();
  for (let c = startCol; c <= endCol; c++) {
    const name = headerState.columnNamesByIndex[c];
    if (name != null && !seen.has(name)) {
      seen.add(name);
      column_names.push(name);
    }
  }
  if (row_ids.length === 0 || column_names.length === 0) return null;
  return { row_ids, column_names };
}

/**
 * Get currently selected cells as row_ids and column_names for the enrichment API.
 * Only includes rows that are visible (not filtered out). Pass univerAPIRef.current and headerStateRef.current (or null).
 */
export function getEnrichmentSelection(
  api: UniverSheetAPILike | null,
  headerState: HeaderStateLike | null
): EnrichmentSelection | null {
  if (!api || !headerState?.columnNamesByIndex?.length) return null;
  const ranges = getSelectedSheetRanges(api);
  if (ranges.length === 0) return null;
  const filteredOutSet = getFilteredOutRowSet(api);
  if (ranges.length === 1) {
    return selectionFromSingleRectangle(ranges[0], headerState, filteredOutSet);
  }
  const explicitCells = expandParsedRangesToExplicitCells(
    ranges,
    headerState,
    filteredOutSet
  );
  if (!explicitCells?.length) return null;
  const row_ids = Array.from(new Set(explicitCells.map((c) => c.row_id))).sort(
    (a, b) => a - b
  );
  const column_names = sortUniqueColumnNames(
    explicitCells.map((c) => c.column_name),
    headerState.columnNamesByIndex
  );
  return { row_ids, column_names, explicitCells };
}

/** True if the column can be targeted by user-initiated selection enrichment (not locked, has enrichment prompt). */
function columnNameIsUserEnrichable(
  column_name: string,
  headerState: HeaderStateLike,
  columnSchemaOverrides: ColumnSchemaOverridesLike
): boolean {
  if (isLockedColumn(column_name)) return false;
  const colIndex = headerState.columnNamesByIndex.indexOf(column_name);
  if (colIndex < 0) return false;
  const def =
    columnSchemaOverrides?.[colIndex]?.definition ??
    getColumnDefinition(headerState.columns[colIndex]);
  return columnDefinitionHasEnrichmentPrompt(def);
}

/**
 * Remove columns that cannot be enriched from a user selection: locked/system columns (e.g. $Domain Name)
 * and columns that are not AI-enabled or have no enrichment prompt. Mixed selections keep only enrichable cells.
 * Returns null if nothing remains.
 */
export function stripNonEnrichableColumnsFromEnrichmentSelection(
  selection: EnrichmentSelection,
  headerState: HeaderStateLike | null,
  columnSchemaOverrides: ColumnSchemaOverridesLike
): EnrichmentSelection | null {
  if (!headerState?.columnNamesByIndex?.length) return null;
  if (selection.explicitCells?.length) {
    const cells = selection.explicitCells.filter((c) =>
      columnNameIsUserEnrichable(
        c.column_name,
        headerState,
        columnSchemaOverrides
      )
    );
    if (cells.length === 0) return null;
    const row_ids = Array.from(new Set(cells.map((c) => c.row_id))).sort(
      (a, b) => a - b
    );
    const column_names = sortUniqueColumnNames(
      cells.map((c) => c.column_name),
      headerState.columnNamesByIndex
    );
    return { row_ids, column_names, explicitCells: cells };
  }
  const column_names = selection.column_names.filter((n) =>
    columnNameIsUserEnrichable(n, headerState, columnSchemaOverrides)
  );
  if (column_names.length === 0) return null;
  return { row_ids: selection.row_ids, column_names };
}

/**
 * Filter a selection to only include columns that are AI-enabled (have a definition) and not locked.
 * Use for scheduled enrichment so users can only schedule cells in valid enrichment columns.
 * Does not check primary/domain column presence (caller may add it later).
 */
export function filterEnrichmentSelectionToAIColumns(
  selection: EnrichmentSelection,
  headerState: HeaderStateLike | null,
  columnSchemaOverrides: ColumnSchemaOverridesLike
): EnrichmentSelection | null {
  if (!headerState?.columnNamesByIndex?.length) return null;
  const validColumnNames: string[] = [];
  for (const column_name of selection.column_names) {
    const colIndex = headerState.columnNamesByIndex.indexOf(column_name);
    if (colIndex < 0) continue;
    if (isLockedColumn(column_name)) continue;
    const def =
      columnSchemaOverrides?.[colIndex]?.definition ??
      getColumnDefinition(headerState.columns[colIndex]);
    if (!def) continue;
    validColumnNames.push(column_name);
  }
  if (validColumnNames.length === 0) return null;
  return { row_ids: selection.row_ids, column_names: validColumnNames };
}

/**
 * Validate enrichment selection: domain column filled, no locked columns, columns AI-enabled.
 * Pass sheet from api, headerStateRef.current, and columnSchemaOverridesRef.current.
 */
function validateOneEnrichmentCell(
  column_name: string,
  headerState: HeaderStateLike,
  columnSchemaOverrides: ColumnSchemaOverridesLike
): { valid: true } | { valid: false; message: string } {
  const colIndex = headerState.columnNamesByIndex.indexOf(column_name);
  if (colIndex < 0) return { valid: true };
  if (isLockedColumn(column_name)) {
    return {
      valid: false,
      message: `The column "${displayColumnName(column_name)}" cannot be enriched.`
    };
  }
  const def =
    columnSchemaOverrides?.[colIndex]?.definition ??
    getColumnDefinition(headerState.columns[colIndex]);
  if (!def) {
    return {
      valid: false,
      message: `Column "${displayColumnName(column_name)}" is not AI-enabled. Select a column with a definition.`
    };
  }
  if (!columnDefinitionHasEnrichmentPrompt(def)) {
    return {
      valid: false,
      message: `Column "${displayColumnName(column_name)}" has no enrichment prompt. Choose a preset or enter custom prompt in the column editor.`
    };
  }
  return { valid: true };
}

export function validateEnrichmentSelection(
  selection: EnrichmentSelection,
  context: {
    sheet: SheetLike | null;
    headerState: HeaderStateLike | null;
    columnSchemaOverrides: ColumnSchemaOverridesLike;
  }
): { valid: true } | { valid: false; message: string } {
  const { sheet, headerState, columnSchemaOverrides } = context;
  if (!headerState?.columnNamesByIndex?.length)
    return { valid: false, message: 'Sheet not ready.' };
  if (!sheet) return { valid: false, message: 'Sheet not ready.' };
  if (selection.explicitCells?.length) {
    for (const { column_name } of selection.explicitCells) {
      const v = validateOneEnrichmentCell(
        column_name,
        headerState,
        columnSchemaOverrides
      );
      if (!v.valid) return v;
    }
    return { valid: true };
  }
  for (const row_id of selection.row_ids) {
    for (const column_name of selection.column_names) {
      const v = validateOneEnrichmentCell(
        column_name,
        headerState,
        columnSchemaOverrides
      );
      if (!v.valid) return v;
    }
  }
  return { valid: true };
}

/**
 * Get all rows that have domain (if present) and at least one empty AI-enabled cell; columns = AI-enabled only.
 * Pass univerAPIRef.current, headerStateRef.current, and columnSchemaOverridesRef.current.
 */
export function getEnrichAllSelection(
  api: UniverSheetAPILike | null,
  headerState: HeaderStateLike | null,
  columnSchemaOverrides: ColumnSchemaOverridesLike
): EnrichmentSelection | null {
  if (!api || !headerState?.columnNamesByIndex?.length) return null;
  const domainColIndex =
    headerState.columnNamesByIndex.indexOf(DOMAIN_COLUMN_NAME);
  const aiColumnIndices: number[] = [];
  const aiColumnNames: string[] = [];
  for (let col = 0; col < headerState.columnNamesByIndex.length; col++) {
    if (isLockedColumn(headerState.columnNamesByIndex[col])) continue;
    const def =
      columnSchemaOverrides?.[col]?.definition ??
      getColumnDefinition(headerState.columns[col]);
    if (columnDefinitionHasEnrichmentPrompt(def)) {
      aiColumnIndices.push(col);
      aiColumnNames.push(headerState.columnNamesByIndex[col]);
    }
  }
  if (aiColumnIndices.length === 0) return null;
  const fWorkbook = api.getActiveWorkbook();
  const fWorksheet = fWorkbook?.getActiveSheet?.();
  const sheet = fWorksheet?.getSheet?.();
  if (!sheet) return null;
  const config = sheet.getConfig();
  const rowCount = config.rowCount ?? 0;
  if (rowCount <= 0) return null;
  const filteredOutSet = getFilteredOutRowSet(api);
  const candidateRows: number[] = [];
  for (let r = 0; r < rowCount; r++) {
    if (filteredOutSet != null && filteredOutSet.has(r)) continue;
    if (domainColIndex >= 0) {
      const raw = sheet.getCellValueOnly(r, domainColIndex);
      const s = normalizeCellValue(raw).trim();
      if (s.length === 0) continue;
    }
    candidateRows.push(r);
  }
  if (candidateRows.length === 0) return null;
  const row_ids: number[] = [];
  for (const r of candidateRows) {
    let hasEmpty = false;
    for (let i = 0; i < aiColumnIndices.length && !hasEmpty; i++) {
      const col = aiColumnIndices[i];
      const raw = sheet.getCellValueOnly(r, col);
      const s = normalizeCellValue(raw).trim();
      if (s.length === 0) hasEmpty = true;
    }
    if (hasEmpty) row_ids.push(r);
  }
  if (row_ids.length === 0 || aiColumnNames.length === 0) return null;
  return { row_ids, column_names: aiColumnNames };
}

/** Get currently selected cells as row_ids and column_names for the enrichment API (from ref getters). */
export function getEnrichmentSelectionFromContext(
  ctx: EnrichmentSelectionContext
): EnrichmentSelection | null {
  return getEnrichmentSelection(ctx.getApi(), ctx.getHeaderState());
}

/** Validate enrichment selection: domain column filled, no locked columns, columns AI-enabled (from ref getters). */
export function validateEnrichmentSelectionFromContext(
  selection: EnrichmentSelection,
  ctx: EnrichmentSelectionContext
): { valid: true } | { valid: false; message: string } {
  const api = ctx.getApi();
  const sheet =
    api?.getActiveWorkbook?.()?.getActiveSheet?.()?.getSheet?.() ?? null;
  return validateEnrichmentSelection(selection, {
    sheet,
    headerState: ctx.getHeaderState(),
    columnSchemaOverrides: ctx.getColumnSchemaOverrides()
  });
}

/** Get all valid enrichable cells (from ref getters). */
export function getEnrichAllSelectionFromContext(
  ctx: EnrichmentSelectionContext
): EnrichmentSelection | null {
  return getEnrichAllSelection(
    ctx.getApi(),
    ctx.getHeaderState(),
    ctx.getColumnSchemaOverrides()
  );
}

/** Create enrichment UI handlers (Enrich button, Enrich All, modal confirm/close). */
export function createEnrichmentHandlers(ctx: EnrichmentHandlersContext): {
  handleEnrichmentClick: () => void;
  handleEnrichAllClick: () => void;
  handleEnrichmentConfirm: () => Promise<void>;
  handleEnrichmentModalClose: () => void;
} {
  return {
    handleEnrichmentClick() {
      if (ctx.activePrioritizationWorkflowId || ctx.prioritizationSheetLock) {
        ctx.showSnackbar('Prioritization in progress. Please wait.', 'warning');
        return;
      }
      if (ctx.activeEnrichmentWorkflowId || ctx.enrichmentSheetLock) {
        ctx.showSnackbar(
          'Another enrichment in progress. Please wait.',
          'warning'
        );
        return;
      }
      const rawSelection = getEnrichmentSelectionFromContext(ctx);
      const selection = rawSelection
        ? stripNonEnrichableColumnsFromEnrichmentSelection(
            rawSelection,
            ctx.getHeaderState(),
            ctx.getColumnSchemaOverrides()
          )
        : null;
      if (
        !selection ||
        selection.row_ids.length === 0 ||
        selection.column_names.length === 0
      ) {
        ctx.showSnackbar(
          'Select at least one AI-enabled cell to enrich.',
          'warning'
        );
        return;
      }
      const validation = validateEnrichmentSelectionFromContext(selection, ctx);
      if (!validation.valid) {
        ctx.showSnackbar(validation.message, 'warning');
        return;
      }
      // User-initiated selection enrichment: allow overwriting filled cells.
      ctx.runEnrichment(selection, { overwriteFilled: true });
    },
    handleEnrichAllClick() {
      if (ctx.activePrioritizationWorkflowId || ctx.prioritizationSheetLock) {
        ctx.showSnackbar('Prioritization in progress. Please wait.', 'warning');
        return;
      }
      if (ctx.activeEnrichmentWorkflowId || ctx.enrichmentSheetLock) {
        ctx.showSnackbar('Enrichment already in progress.', 'warning');
        return;
      }
      const api = ctx.getApi();
      const sheet =
        api?.getActiveWorkbook?.()?.getActiveSheet?.()?.getSheet?.() ?? null;
      const headerState = ctx.getHeaderState();
      const selection = getEnrichAllSelectionFromContext(ctx);
      if (
        !selection ||
        selection.row_ids.length === 0 ||
        selection.column_names.length === 0
      ) {
        ctx.showSnackbar(
          'No enrichable cells found. Add AI-enabled columns or ensure rows have valid first-column data and at least one empty AI cell.',
          'warning'
        );
        return;
      }
      let cellCount = selection.row_ids.length * selection.column_names.length;
      if (sheet && headerState?.columnNamesByIndex?.length) {
        cellCount = 0;
        for (const row_id of selection.row_ids) {
          for (const column_name of selection.column_names) {
            const colIndex =
              headerState.columnNamesByIndex.indexOf(column_name);
            if (colIndex < 0) continue;
            const raw = sheet.getCellValueOnly(row_id, colIndex);
            const s = normalizeCellValue(raw).trim();
            if (s.length > 0) continue;
            cellCount += 1;
          }
        }
      }
      ctx.setEnrichmentPending({ ...selection, cell_count: cellCount });
      ctx.setEnrichmentModalOpen(true);
    },
    async handleEnrichmentConfirm() {
      const pending = ctx.enrichmentPending;
      if (!pending) return;
      if (ctx.activePrioritizationWorkflowId || ctx.prioritizationSheetLock) {
        ctx.showSnackbar('Prioritization in progress. Please wait.', 'warning');
        return;
      }
      const validation = validateEnrichmentSelectionFromContext(pending, ctx);
      if (!validation.valid) {
        ctx.showSnackbar(validation.message, 'warning');
        return;
      }
      // Enrich-all: do not overwrite already filled cells.
      await ctx.runEnrichment(pending, { overwriteFilled: false });
    },
    handleEnrichmentModalClose() {
      ctx.setEnrichmentModalOpen(false);
      ctx.setEnrichmentPending(null);
    }
  };
}

/** Ask Univer to repaint the sheet so custom cell renders (e.g. enriching highlight) are drawn. */
export function requestSheetRepaint(ctx: EnrichmentRepaintContext): void {
  requestAnimationFrame(() => {
    try {
      const univer = ctx.getUniver();
      const api = ctx.getApi();
      if (!univer || !api) return;
      const injector = univer.__getInjector() as {
        get: (id: unknown) => unknown;
      };
      const renderManager = injector.get(IRenderManagerService) as
        | IRenderManagerService
        | undefined;
      const unitId = api.getActiveWorkbook?.()?.getId?.();
      if (renderManager && typeof unitId === 'string') {
        const render = renderManager.getRenderById(unitId) as
          | {
              scene?: { makeDirty?: (state?: boolean) => unknown };
              mainComponent?: { makeDirty?: (state?: boolean) => unknown };
            }
          | undefined;
        if (render?.scene?.makeDirty) render.scene.makeDirty(true);
        else if (render?.mainComponent?.makeDirty)
          render.mainComponent.makeDirty(true);
      }
      const fWorksheet = api.getActiveWorkbook?.()?.getActiveSheet?.() as
        | { refreshCanvas?: () => void }
        | null
        | undefined;
      fWorksheet?.refreshCanvas?.();
    } catch {
      // ignore
    }
  });
}

type CellRedrawCoord = { row_id: number; colIndex: number };

function enrichmentSelectionCoords(
  selection: EnrichmentSelection,
  columnNamesByIndex: string[]
): CellRedrawCoord[] {
  const coords: CellRedrawCoord[] = [];
  selection.row_ids.forEach((row_id) => {
    selection.column_names.forEach((colName) => {
      const colIndex = columnNamesByIndex.indexOf(colName);
      if (colIndex >= 0) coords.push({ row_id, colIndex });
    });
  });
  return coords;
}

function cellKeyCoords(keys: Set<string>, columnCount: number): CellRedrawCoord[] {
  const coords: CellRedrawCoord[] = [];
  keys.forEach((k) => {
    const sep = k.indexOf(':');
    if (sep < 0) return;
    const row_id = Number(k.slice(0, sep));
    const colIndex = Number(k.slice(sep + 1));
    if (
      !Number.isFinite(row_id) ||
      !Number.isFinite(colIndex) ||
      colIndex < 0 ||
      colIndex >= columnCount
    ) {
      return;
    }
    coords.push({ row_id, colIndex });
  });
  return coords;
}

/**
 * Force Univer to run the cell render path (custom render) via no-op set-range-values.
 * A single-cell noop only dirties that cell; batching all coords avoids missing highlights.
 * TODO: find a way to render in-flight cells without manually dirtying every coord.
 */
function forceCellsRedrawByCoords(
  coords: CellRedrawCoord[],
  ctx: ForceEnrichingRedrawContext
): void {
  const api = ctx.getApi();
  const state = ctx.getHeaderState();
  if (!api || !state?.columnNamesByIndex?.length) return;
  const fWorksheet = api.getActiveWorkbook?.()?.getActiveSheet?.();
  const sheet = fWorksheet?.getSheet?.();
  if (!sheet) return;

  const value: Record<
    number,
    Record<number, ReturnType<typeof ctx.cellValueToUniverCell>>
  > = {};
  let minR = Infinity;
  let maxR = -1;
  let minC = Infinity;
  let maxC = -1;

  coords.forEach(({ row_id, colIndex }) => {
    const rawVal = sheet.getCellValueOnly(row_id, colIndex);
    const colType = state.columns[colIndex]
      ? getColTypeFromSchema(state.columns[colIndex] as ColumnSchemaWithData)
      : undefined;
    if (!value[row_id]) value[row_id] = {};
    value[row_id][colIndex] = ctx.cellValueToUniverCell(
      normalizeCellValue(rawVal),
      { colType }
    );
    minR = Math.min(minR, row_id);
    maxR = Math.max(maxR, row_id);
    minC = Math.min(minC, colIndex);
    maxC = Math.max(maxC, colIndex);
  });
  if (minR > maxR || minC > maxC || Object.keys(value).length === 0) return;

  const enrichmentRef = ctx.isApplyingEnrichmentCellRef;
  if (enrichmentRef) enrichmentRef.current = true;
  try {
    const range = {
      startRow: minR,
      endRow: maxR,
      startColumn: minC,
      endColumn: maxC
    };
    api.executeCommand('sheet.command.set-range-values', { range, value });
  } catch {
    // ignore
  } finally {
    if (enrichmentRef)
      queueMicrotask(() => {
        enrichmentRef.current = false;
      });
  }
}

/**
 * Force Univer to run the enriching-cells custom render for an enrichment selection.
 */
export function forceEnrichingCellsRedraw(
  selection: EnrichmentSelection,
  ctx: ForceEnrichingRedrawContext
): void {
  const state = ctx.getHeaderState();
  if (
    !state?.columnNamesByIndex?.length ||
    selection.row_ids.length === 0 ||
    selection.column_names.length === 0
  ) {
    return;
  }
  forceCellsRedrawByCoords(
    enrichmentSelectionCoords(selection, state.columnNamesByIndex),
    ctx
  );
}

/**
 * Like `forceEnrichingCellsRedraw` but for `row_id:colIndex` keys (e.g. prioritizing cells).
 */
export function forceCellKeysRedraw(
  keys: Set<string>,
  ctx: ForceEnrichingRedrawContext
): void {
  const state = ctx.getHeaderState();
  if (!state?.columnNamesByIndex?.length || keys.size === 0) return;
  forceCellsRedrawByCoords(
    cellKeyCoords(keys, state.columnNamesByIndex.length),
    ctx
  );
}

/** Create a runEnrichment(selection) function that uses the current context. Pass a getter so context is read at call time. */
export function createRunEnrichment(
  getContext: () => {
    campaignId: string | undefined;
    viewType: CampaignSpreadsheetViewType | undefined;
    runCtx: RunEnrichmentContext;
  }
): (
  selection: EnrichmentSelection,
  options?: { overwriteFilled?: boolean }
) => Promise<void> {
  return async (
    selection: EnrichmentSelection,
    options?: { overwriteFilled?: boolean }
  ) => {
    const { campaignId, viewType, runCtx } = getContext();
    if (!campaignId) return;
    await runEnrichment(campaignId, viewType, selection, runCtx, options);
  };
}

/** Filter selection to only rows that have a domain value. Rows without domain are ignored. */
function filterSelectionToRowsWithDomain(
  selection: EnrichmentSelection,
  sheet: { getCellValueOnly(row: number, col: number): unknown } | null,
  headerState: HeaderStateLike | null
): EnrichmentSelection {
  if (!sheet || !headerState?.columnNamesByIndex?.length) return selection;
  const domainColIndex =
    headerState.columnNamesByIndex.indexOf(DOMAIN_COLUMN_NAME);
  if (domainColIndex < 0) return selection;
  if (selection.explicitCells?.length) {
    const filteredCells = selection.explicitCells.filter((cell) => {
      const raw = sheet.getCellValueOnly(cell.row_id, domainColIndex);
      return normalizeCellValue(raw).trim().length > 0;
    });
    if (filteredCells.length === 0) {
      return { row_ids: [], column_names: [], explicitCells: [] };
    }
    const row_ids = Array.from(
      new Set(filteredCells.map((c) => c.row_id))
    ).sort((a, b) => a - b);
    const column_names = sortUniqueColumnNames(
      filteredCells.map((c) => c.column_name),
      headerState.columnNamesByIndex
    );
    return { row_ids, column_names, explicitCells: filteredCells };
  }
  const rowsWithDomain = selection.row_ids.filter((row_id) => {
    const raw = sheet.getCellValueOnly(row_id, domainColIndex);
    const s = normalizeCellValue(raw).trim();
    return s.length > 0;
  });
  return { row_ids: rowsWithDomain, column_names: selection.column_names };
}

/** Run enrichment for the given selection. Used by both selected-cells (no modal) and enrich-all (after modal confirm). */
export async function runEnrichment(
  campaignId: string,
  viewType: CampaignSpreadsheetViewType | undefined,
  selection: EnrichmentSelection,
  ctx: RunEnrichmentContext,
  options?: { overwriteFilled?: boolean }
): Promise<void> {
  const overwriteFilled = options?.overwriteFilled ?? true;
  if (!campaignId) return;
  const api = ctx.getApi();
  const sheet =
    api?.getActiveWorkbook?.()?.getActiveSheet?.()?.getSheet?.() ?? null;
  const state = ctx.getHeaderState();
  const filteredSelection = filterSelectionToRowsWithDomain(
    selection,
    sheet,
    state
  );
  if (filteredSelection.row_ids.length === 0) {
    ctx.showSnackbar(
      'No rows with a domain in the selection. Rows without a domain are skipped.',
      'warning'
    );
    return;
  }
  ctx.setEnrichmentSheetLock(true);
  if (state && state.columnNamesByIndex?.length) {
    if (!sheet || overwriteFilled) {
      // When overwriting is allowed or sheet is unavailable, mark all cells in the selection as enriching.
      if (filteredSelection.explicitCells?.length) {
        for (const { row_id, column_name } of filteredSelection.explicitCells) {
          const col = state.columnNamesByIndex.indexOf(column_name);
          if (col >= 0) ctx.enrichingCellsRef.current.add(`${row_id}:${col}`);
        }
      } else {
        for (const row_id of filteredSelection.row_ids) {
          for (const colName of filteredSelection.column_names) {
            const col = state.columnNamesByIndex.indexOf(colName);
            if (col >= 0) ctx.enrichingCellsRef.current.add(`${row_id}:${col}`);
          }
        }
      }
    } else {
      // Enrich-all path: only mark currently empty cells as enriching.
      if (filteredSelection.explicitCells?.length) {
        for (const { row_id, column_name } of filteredSelection.explicitCells) {
          const col = state.columnNamesByIndex.indexOf(column_name);
          if (col < 0) continue;
          const raw = sheet.getCellValueOnly(row_id, col);
          const s = normalizeCellValue(raw).trim();
          if (s.length > 0) continue;
          ctx.enrichingCellsRef.current.add(`${row_id}:${col}`);
        }
      } else {
        for (const row_id of filteredSelection.row_ids) {
          for (const colName of filteredSelection.column_names) {
            const col = state.columnNamesByIndex.indexOf(colName);
            if (col < 0) continue;
            const raw = sheet.getCellValueOnly(row_id, col);
            const s = normalizeCellValue(raw).trim();
            if (s.length > 0) continue;
            ctx.enrichingCellsRef.current.add(`${row_id}:${col}`);
          }
        }
      }
    }
    ctx.setEnrichingCellsCount(ctx.enrichingCellsRef.current.size);
    ctx.requestSheetRepaint();
    requestAnimationFrame(() =>
      forceEnrichingCellsRedraw(filteredSelection, ctx)
    );
  }
  try {
    let cells: { row_id: number; column_name: string }[];
    if (!sheet || overwriteFilled || !state?.columnNamesByIndex?.length) {
      // Selection enrichment (overwrite) or missing sheet: send all cells in the selection.
      if (filteredSelection.explicitCells?.length) {
        cells = filteredSelection.explicitCells.map((c) => ({
          row_id: c.row_id,
          column_name: c.column_name
        }));
      } else {
        cells = filteredSelection.row_ids.flatMap((row_id) =>
          filteredSelection.column_names.map((column_name) => ({
            row_id,
            column_name
          }))
        );
      }
    } else {
      // Enrich-all path: only send currently empty cells.
      cells = [];
      if (filteredSelection.explicitCells?.length) {
        for (const { row_id, column_name } of filteredSelection.explicitCells) {
          const colIndex = state.columnNamesByIndex.indexOf(column_name);
          if (colIndex < 0) continue;
          const raw = sheet.getCellValueOnly(row_id, colIndex);
          const s = normalizeCellValue(raw).trim();
          if (s.length > 0) continue;
          cells.push({ row_id, column_name });
        }
      } else {
        for (const row_id of filteredSelection.row_ids) {
          for (const column_name of filteredSelection.column_names) {
            const colIndex = state.columnNamesByIndex.indexOf(column_name);
            if (colIndex < 0) continue;
            const raw = sheet.getCellValueOnly(row_id, colIndex);
            const s = normalizeCellValue(raw).trim();
            if (s.length > 0) continue;
            cells.push({ row_id, column_name });
          }
        }
      }
      if (cells.length === 0) {
        ctx.enrichingCellsRef.current.clear();
        ctx.setEnrichingCellsCount(0);
        ctx.setEnrichmentSheetLock(false);
        ctx.showSnackbar('No empty cells to enrich in the selection.', 'info');
        return;
      }
    }
    // Normal enrichment (overwrite): clear backend so sync does not reload old values; then run execute.
    if (overwriteFilled && sheet && state?.columnNamesByIndex?.length) {
      const cellsToClear: Cell[] = [];
      if (filteredSelection.explicitCells?.length) {
        for (const { row_id, column_name } of filteredSelection.explicitCells) {
          const colIndex = state.columnNamesByIndex.indexOf(column_name);
          if (colIndex < 0) continue;
          const raw = sheet.getCellValueOnly(row_id, colIndex);
          const s = normalizeCellValue(raw).trim();
          if (s.length > 0)
            cellsToClear.push({ row_id, column_name, cell_value: '' });
        }
      } else {
        for (const row_id of filteredSelection.row_ids) {
          for (const column_name of filteredSelection.column_names) {
            const colIndex = state.columnNamesByIndex.indexOf(column_name);
            if (colIndex < 0) continue;
            const raw = sheet.getCellValueOnly(row_id, colIndex);
            const s = normalizeCellValue(raw).trim();
            if (s.length > 0)
              cellsToClear.push({ row_id, column_name, cell_value: '' });
          }
        }
      }
      if (cellsToClear.length > 0) {
        await putCells(campaignId, cellsToClear, viewType);
      }
    }
    const { temporal_workflow_id } = await executeCells(
      campaignId,
      { cells },
      viewType
    );
    ctx.setEnrichmentModalOpen(false);
    ctx.setEnrichmentPending(null);
    ctx.setActiveEnrichmentWorkflowId(temporal_workflow_id);

    ctx.enrichmentStreamCancelRef.current?.();
    ctx.enrichmentStreamCancelRef.current = streamExecuteCells(
      campaignId,
      temporal_workflow_id,
      {
        onSyncRequired: () => {
          ctx.requestSync?.();
        },
        onComplete: () => {
          ctx.requestSync?.();
          ctx.enrichingCellsRef.current.clear();
          ctx.setEnrichingCellsCount(0);
          ctx.setActiveEnrichmentWorkflowId(null);
          ctx.setEnrichmentSheetLock(false);
          ctx.enrichmentStreamCancelRef.current = null;
          ctx.requestSheetRepaint();
        },
        onError: (message: string) => {
          ctx.enrichingCellsRef.current.clear();
          ctx.setEnrichingCellsCount(0);
          ctx.setActiveEnrichmentWorkflowId(null);
          ctx.setEnrichmentSheetLock(false);
          ctx.enrichmentStreamCancelRef.current = null;
          ctx.requestSheetRepaint();
          ctx.showSnackbar(message, 'error');
        }
      },
      viewType
    );
  } catch (err) {
    ctx.enrichingCellsRef.current.clear();
    ctx.setEnrichingCellsCount(0);
    ctx.setEnrichmentSheetLock(false);
    ctx.showSnackbar(
      err instanceof Error ? err.message : 'Enrichment request failed.',
      'error'
    );
  }
}

/**
 * Subscribe to active enrichment workflow on mount/refresh; reconnects to stream if backend has an active workflow.
 * Returns a cleanup function.
 */
export function subscribeToActiveEnrichmentWorkflow(
  campaignId: string | undefined,
  viewType: CampaignSpreadsheetViewType | undefined,
  ctx: ReconnectEnrichmentContext
): () => void {
  if (!campaignId) return () => {};
  let cancelled = false;
  let cancelStream: (() => void) | null = null;
  getActiveCellExecutorWorkflowId(campaignId, viewType)
    .then((res) => {
      if (cancelled || !res?.workflow_id) return;
      ctx.setActiveEnrichmentWorkflowId(res.workflow_id);
      ctx.enrichmentStreamCancelRef.current?.();
      cancelStream = ctx.enrichmentStreamCancelRef.current = streamExecuteCells(
        campaignId,
        res.workflow_id,
        {
          onSyncRequired: () => {
            if (!cancelled) {
              ctx.requestSync?.();
            }
          },
          onComplete: () => {
            if (!cancelled) {
              ctx.requestSync?.();
              ctx.enrichingCellsRef.current.clear();
              ctx.setEnrichingCellsCount(0);
              ctx.setActiveEnrichmentWorkflowId(null);
              ctx.enrichmentStreamCancelRef.current = null;
            }
            ctx.requestSheetRepaint();
          },
          onError: (message: string) => {
            if (!cancelled) {
              ctx.enrichingCellsRef.current.clear();
              ctx.setEnrichingCellsCount(0);
              ctx.setActiveEnrichmentWorkflowId(null);
              ctx.showSnackbar(message, 'error');
            }
            ctx.enrichmentStreamCancelRef.current = null;
            ctx.requestSheetRepaint();
          }
        },
        viewType
      );
    })
    .catch(() => {
      if (!cancelled) ctx.setActiveEnrichmentWorkflowId(null);
    });
  return () => {
    cancelled = true;
    cancelStream?.();
  };
}
